import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { parentAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Bell, Mail, Users, CheckCircle, Clock, AlertCircle,
  ChevronDown, ChevronUp, RefreshCw, FileText
} from 'lucide-react';

/**
 * ParentUpdates — Synthesized timeline replacing the old Inbox.
 *
 * Answers:
 * - What happened?
 * - What did Kivo do?
 * - What did I miss?
 * - What matters most?
 *
 * Data sources:
 * - parentAPI.getToday() → recent_inbox (school emails + tasks), nanny_logs (caregiver + system)
 * - Synthesis is frontend-only — no new backend endpoints needed.
 */
export const ParentUpdates = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const urlFamilyId = searchParams.get('family_id');
  const familyId = (urlFamilyId && urlFamilyId !== 'null')
    ? urlFamilyId
    : (user?.familyId || localStorage.getItem('family_id'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dayGroups, setDayGroups] = useState([]);
  const [expandedEmail, setExpandedEmail] = useState(highlightId || null);

  // Read status (preserved from old Inbox for backward compat)
  const [readIds, setReadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('kivo_inbox_read') || '[]')); }
    catch { return new Set(); }
  });

  const markRead = (id) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem('kivo_inbox_read', JSON.stringify([...next]));
      return next;
    });
  };

  useEffect(() => {
    if (familyId) loadData();
  }, [familyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await parentAPI.getToday(familyId);
      if (response.data?.ok) {
        const groups = synthesize(response.data);
        setDayGroups(groups);
      } else {
        setError(response.data?.error || 'Failed to load updates');
      }
    } catch (err) {
      console.error('Failed to load updates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Synthesis engine ───────────────────────────────────────

  const synthesize = (data) => {
    const entries = [];

    // 1. School emails → entries
    for (const item of (data.recent_inbox || [])) {
      const date = toDateKey(item.created_at);
      const taskCount = (item.tasks || []).length || item.tasks_created || 0;
      const openTasks = (item.tasks || []).filter(t =>
        ['open', 'OPEN', 'pending'].includes(t.status)
      );
      entries.push({
        type: 'school',
        date,
        timestamp: item.created_at,
        id: item.id,
        subject: item.subject || 'No subject',
        taskCount,
        openTaskCount: openTasks.length,
        tasks: item.tasks || [],
        bodyText: item.body_text || '',
        schoolEmailId: item.school_email_id,
      });
    }

    // 2. Caregiver logs → synthesized per-day entry
    const caregiverByDay = {};
    for (const log of (data.nanny_logs || [])) {
      if (log.source !== 'caregiver') continue;
      const date = toDateKey(log.created_at);
      if (!caregiverByDay[date]) caregiverByDay[date] = [];
      caregiverByDay[date].push(log);
    }

    for (const [date, logs] of Object.entries(caregiverByDay)) {
      const signals = extractCaregiverSignals(logs);
      entries.push({
        type: 'caregiver',
        date,
        timestamp: logs[0].created_at,
        id: `caregiver-${date}`,
        signals,
        logCount: logs.length,
      });
    }

    // 3. System alerts (only actionable ones — missed check-in, urgent alerts)
    for (const log of (data.nanny_logs || [])) {
      if (log.source === 'caregiver') continue;
      const val = log.value_json || {};
      const logType = log.log_type || '';
      // Only surface notable system events
      if (logType.includes('missed') || logType.includes('alert') || logType.includes('urgent')) {
        const date = toDateKey(log.created_at);
        entries.push({
          type: 'system',
          date,
          timestamp: log.created_at,
          id: log.id,
          logType,
          message: val.message || val.summary || formatLogType(logType),
        });
      }
    }

    // Group by day, sort days desc, sort entries within day by timestamp desc
    const grouped = {};
    for (const entry of entries) {
      if (!grouped[entry.date]) grouped[entry.date] = [];
      grouped[entry.date].push(entry);
    }

    const sortedDays = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
    return sortedDays.map(date => ({
      date,
      label: formatDayLabel(date),
      entries: grouped[date].sort((a, b) =>
        (b.timestamp || '').localeCompare(a.timestamp || '')
      ),
    }));
  };

  const extractCaregiverSignals = (logs) => {
    const signals = {
      pickup: null,
      mood: null,
      lunch: null,
      notes: [],
    };
    for (const log of logs) {
      const val = log.value_json || {};
      if (val.pickup_time || val.arrived_at || val.pickup_at || val.time_display) {
        signals.pickup = val.pickup_time || val.arrived_at || val.pickup_at || val.time_display;
      }
      if (val.mood || val.mood_text || val.child_mood) {
        signals.mood = val.mood || val.mood_text || val.child_mood;
      }
      if (val.lunch_eaten !== undefined) {
        signals.lunch = val.lunch_eaten;
      }
      if (val.notes || val.note || val.message) {
        const note = val.notes || val.note || val.message;
        if (note && !signals.notes.includes(note)) signals.notes.push(note);
      }
    }
    return signals;
  };

  // ─── Formatting helpers ─────────────────────────────────────

  const toDateKey = (dateStr) => {
    if (!dateStr) return 'unknown';
    return dateStr.slice(0, 10);
  };

  const formatDayLabel = (dateKey) => {
    if (dateKey === 'unknown') return 'Earlier';
    const date = new Date(dateKey + 'T12:00:00');
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    const yesterday = new Date(now - 86400000).toISOString().slice(0, 10);

    if (dateKey === today) return 'Today';
    if (dateKey === yesterday) return 'Yesterday';
    return date.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  const formatLogType = (logType) => {
    return (logType || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // ─── Render ─────────────────────────────────────────────────

  if (!familyId) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">No Family Selected</h2>
        <Link to="/login" className="text-primary hover:underline">Please log in</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6" data-testid="parent-updates">
        <UpdatesHeader />
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-slate-200 border-t-primary rounded-full mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading updates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="parent-updates">
        <UpdatesHeader />
        <div className="bg-red-50 border border-red-100 text-red-700 px-6 py-4 rounded-xl flex items-center justify-between">
          <span className="text-sm">{error}</span>
          <button
            onClick={loadData}
            className="ml-4 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-updates">
      <UpdatesHeader onRefresh={loadData} />

      {dayGroups.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
          <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-base font-medium text-slate-700">All clear</p>
          <p className="text-sm text-slate-500 mt-1">No recent updates. Kivo will notify you when something happens.</p>
        </div>
      ) : (
        dayGroups.map(group => (
          <div key={group.date}>
            {/* Day header */}
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">{group.label}</h2>
              <div className="flex-1 h-px bg-slate-100" />
            </div>

            <div className="space-y-2">
              {group.entries.map(entry => {
                if (entry.type === 'caregiver') return <CaregiverEntry key={entry.id} entry={entry} />;
                if (entry.type === 'school') return (
                  <SchoolEntry
                    key={entry.id}
                    entry={entry}
                    isRead={readIds.has(entry.id)}
                    isExpanded={expandedEmail === entry.id}
                    onToggle={() => {
                      markRead(entry.id);
                      setExpandedEmail(prev => prev === entry.id ? null : entry.id);
                    }}
                  />
                );
                if (entry.type === 'system') return <SystemEntry key={entry.id} entry={entry} />;
                return null;
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ─── Sub-components ────────────────────────────────────────────

const UpdatesHeader = ({ onRefresh }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
        <Bell className="w-5 h-5 text-blue-500" />
      </div>
      <div>
        <h1 className="text-xl font-bold text-slate-800">Updates</h1>
        <p className="text-sm text-slate-500">What happened & what Kivo did</p>
      </div>
    </div>
    {onRefresh && (
      <button
        onClick={onRefresh}
        className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
        title="Refresh"
      >
        <RefreshCw className="w-4 h-4" />
      </button>
    )}
  </div>
);

const CaregiverEntry = ({ entry }) => {
  const { signals } = entry;
  const parts = [];

  if (signals.pickup) parts.push(`Picked up at ${signals.pickup}`);
  if (signals.mood) parts.push(`mood ${signals.mood}`);
  if (signals.lunch === true || signals.lunch === 'yes') parts.push('had lunch');
  if (signals.lunch === false || signals.lunch === 'no') parts.push('skipped lunch');

  const summary = parts.length > 0
    ? parts.join(' · ')
    : `${entry.logCount} update${entry.logCount > 1 ? 's' : ''} received`;

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid="update-caregiver">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Users className="w-4 h-4 text-green-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800">Caregiver update</p>
          <p className="text-sm text-slate-600 mt-0.5">{summary}</p>
          {signals.notes.length > 0 && (
            <div className="mt-2">
              {signals.notes.map((note, i) => (
                <p key={i} className="text-xs text-slate-500 italic">"{note}"</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SchoolEntry = ({ entry, isRead, isExpanded, onToggle }) => {
  const hasActions = entry.openTaskCount > 0;

  return (
    <div
      className={`bg-white rounded-xl border p-4 transition-colors cursor-pointer ${
        isRead ? 'border-slate-100' : 'border-blue-200 bg-blue-50/30'
      }`}
      onClick={onToggle}
      data-testid="update-school"
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
          hasActions ? 'bg-amber-50' : 'bg-slate-50'
        }`}>
          <Mail className={`w-4 h-4 ${hasActions ? 'text-amber-600' : 'text-slate-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium truncate ${isRead ? 'text-slate-700' : 'text-slate-900'}`}>
              {entry.subject}
            </p>
            {!isRead && (
              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
            )}
          </div>

          {/* Synthesis line */}
          <p className="text-xs text-slate-500 mt-0.5">
            {entry.taskCount > 0
              ? `${entry.taskCount} task${entry.taskCount > 1 ? 's' : ''} extracted${entry.openTaskCount > 0 ? ` · ${entry.openTaskCount} open` : ' · all done'}`
              : 'No action needed'
            }
            <span className="ml-2 text-slate-400">{formatTimeCompact(entry.timestamp)}</span>
          </p>

          {/* Expanded: tasks + body */}
          {isExpanded && (
            <div className="mt-3 space-y-2" onClick={e => e.stopPropagation()}>
              {entry.tasks.length > 0 && (
                <div className="space-y-1">
                  {entry.tasks.map(task => (
                    <div key={task.id} className="flex items-center gap-2 text-xs">
                      {['done', 'cancelled'].includes(task.status) ? (
                        <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      ) : (
                        <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      )}
                      <span className={`${['done', 'cancelled'].includes(task.status) ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                        {task.title}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {entry.bodyText && (
                <details className="group">
                  <summary className="text-xs text-primary cursor-pointer hover:underline flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    View original email
                  </summary>
                  <div className="mt-2 p-3 bg-slate-50 rounded-lg text-xs text-slate-600 whitespace-pre-wrap max-h-60 overflow-y-auto">
                    {entry.bodyText}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 mt-1">
          {isExpanded
            ? <ChevronUp className="w-4 h-4 text-slate-400" />
            : <ChevronDown className="w-4 h-4 text-slate-400" />
          }
        </div>
      </div>
    </div>
  );
};

const SystemEntry = ({ entry }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-4" data-testid="update-system">
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0 mt-0.5">
        <AlertCircle className="w-4 h-4 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-600">{entry.message}</p>
        <p className="text-xs text-slate-400 mt-0.5">{formatTimeCompact(entry.timestamp)}</p>
      </div>
    </div>
  </div>
);

// Compact time format for inline use
function formatTimeCompact(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

export default ParentUpdates;
